import { LOGIN_ROLES, removeEmptyFields } from "@/lib/utils";
import { useProfilesByRoleQuery } from "@/services/employeeManagement/useProfilesByRoleQuery";
import { useCrmResourceManagementMutation } from "@/services/school/crm/resource/useCrmResourceManagementMutation";
import { useGetCrmResourceManagementQuery } from "@/services/school/crm/resource/useGetCrmResourceManagementQuery";
import { useUpdateCrmResourceManagementMutation } from "@/services/school/crm/resource/useUpdateCrmResourceManagementMutation";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useParams } from "react-router-dom";
import {
  COMMENT,
  CRM_OPS_STATUS,
  REGION_HEAD_ID,
  RELATIONSHIP_MANAGER_ID,
  SCHOOL_EDUCATOR_EMAIL,
  SCHOOL_EDUCATOR_MOBILE,
  SCHOOL_EDUCATOR_NAME
} from "./consts";
import { FormValues, schema } from "./validations";

export const useAddEditResource = () => {
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState(true);

  // Fetch existing CRM resource management data
  const { data: existingData, isLoading: isLoadingExisting } =
    useGetCrmResourceManagementQuery(id || "", {
      enabled: !!id,
      retry: false, // Don't retry on 404 errors
    });

  const isEditMode = !!existingData?.data;

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      [REGION_HEAD_ID]: "",
      [RELATIONSHIP_MANAGER_ID]: "",
      [SCHOOL_EDUCATOR_NAME]: "",
      [SCHOOL_EDUCATOR_MOBILE]: "",
      [SCHOOL_EDUCATOR_EMAIL]: "",
      [CRM_OPS_STATUS]: "Green",
      [COMMENT]: "",
    },
  });

  // Fetch region heads
  const { data: regionHeadsData, isLoading: isLoadingRegionHeads } =
    useProfilesByRoleQuery(LOGIN_ROLES.REGION_HEAD);

  // Fetch relationship managers
  const {
    data: relationshipManagersData,
    isLoading: isLoadingRelationshipManagers,
  } = useProfilesByRoleQuery(LOGIN_ROLES.RELATIONSHIP_MANAGER);

  // CRM Resource Management mutations
  const { mutate: createCrmResourceManagement, isPending: isCreating } =
    useCrmResourceManagementMutation({
      onSuccess: () => {
        // form.reset();
      },
    });

  const { mutate: updateCrmResourceManagement, isPending: isUpdating } =
    useUpdateCrmResourceManagementMutation({
      onSuccess: () => {
        // Don't reset form in edit mode, keep the updated values
      },
    });

  const isSubmitting = isCreating || isUpdating;

  const regionHeadOptions =
    regionHeadsData?.data?.[0]?.records?.map((profile: any) => ({
      label: profile.name,
      value: profile.profile_id,
    })) || [];

  const relationshipManagerOptions =
    relationshipManagersData?.data?.[0]?.records?.map((profile: any) => ({
      label: profile.name,
      value: profile.profile_id,
    })) || [];

  // Watch selected values to get profile details
  const selectedRegionHeadId = useWatch({
    control: form.control,
    name: REGION_HEAD_ID,
  });

  const selectedRelationshipManagerId = useWatch({
    control: form.control,
    name: RELATIONSHIP_MANAGER_ID,
  });

  // Get selected profile details
  const selectedRegionHead = regionHeadsData?.data?.[0]?.records?.find(
    (profile: any) => profile.profile_id === selectedRegionHeadId
  );

  const selectedRelationshipManager =
    relationshipManagersData?.data?.[0]?.records?.find(
      (profile: any) => profile.profile_id === selectedRelationshipManagerId
    );

  // Populate form with existing data when in edit mode
  useEffect(() => {
    if (existingData?.data && isEditMode) {
      const existing = existingData.data;
      form.reset({
        [REGION_HEAD_ID]: existing.regionHeadId ?? "",
        [RELATIONSHIP_MANAGER_ID]: existing.relationshipManagerId ?? "",
        [SCHOOL_EDUCATOR_NAME]: existing.schoolEducatorName ?? "",
        [SCHOOL_EDUCATOR_MOBILE]: existing.schoolEducatorMobileNumber ?? "",
        [SCHOOL_EDUCATOR_EMAIL]: existing.schoolEducatorEmailId ?? "",
        [CRM_OPS_STATUS]: existing.crmOpsStatus,
        [COMMENT]: existing.comment ?? "",
      });
    }
    setIsLoading(false);
  }, [existingData, isEditMode, form]);

  const onSubmit = (values: FormValues) => {
    if (!id) return;

    console.log({ values });
    
    if (isEditMode) {
      // Update existing record
      const updateData = {
        crmOpsStatus: values[CRM_OPS_STATUS] as "Green" | "Yellow" | "Red",
        comment: values[COMMENT] || "",
        regionHeadId: values[REGION_HEAD_ID],
        relationshipManagerId: values[RELATIONSHIP_MANAGER_ID],
        schoolEducatorName: values[SCHOOL_EDUCATOR_NAME],
        schoolEducatorMobileNumber: values[SCHOOL_EDUCATOR_MOBILE],
        schoolEducatorEmailId: values[SCHOOL_EDUCATOR_EMAIL],
      };
      updateCrmResourceManagement({ schoolId: id, data: updateData });
    } else {
      // Create new record
      let data = removeEmptyFields(values);
      data.schoolId = id;
      createCrmResourceManagement(data);
    }
  };

  return {
    form,
    onSubmit,
    isSubmitting,
    regionHeadOptions,
    relationshipManagerOptions,
    isLoadingRegionHeads,
    isLoadingRelationshipManagers,
    selectedRegionHead,
    selectedRelationshipManager,
    isEditMode,
    isLoadingExisting: isLoadingExisting || isLoading || relationshipManagerOptions.length <= 0 || regionHeadOptions.length <= 0,
  };
};
